import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { BRAND_SANS_FONT } from '../../shared/design/theme';

/**
 * Shared article renderer used by both the standalone BlogPostPage
 * and the lightbox BlogPostModal. Renders eyebrow meta, title,
 * accent divider, and the content blocks (paragraphs + headings).
 *
 * Deliberately does NOT include any outer chrome (AppBar, Footer, Dialog)
 * so each caller can wrap it appropriately.
 */
export default function BlogArticleBody({ post, compact = false }) {
  const theme = useTheme();

  if (!post) return null;

  // Slightly tighter type scale when rendered inside the modal
  const titleSize = compact
    ? { xs: '1.2rem', sm: '1.3rem', md: '1.35rem' }
    : { xs: '1.3rem', sm: '1.4rem', md: '1.5rem' };

  const leadBody = compact
    ? { xs: '0.92rem', md: '0.95rem' }
    : { xs: '0.95rem', md: '0.98rem' };

  const bodyBody = compact
    ? { xs: '0.86rem', md: '0.88rem' }
    : { xs: '0.88rem', md: '0.9rem' };

  const headingSize = compact
    ? { xs: '1rem', md: '1.05rem' }
    : { xs: '1.05rem', md: '1.15rem' };

  return (
    <>
      {/* Eyebrow meta: CATEGORY · DATE · READ TIME */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.25,
          mb: 3,
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.7rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: alpha(theme.palette.text.primary, 0.55),
        }}
      >
        <Box component="span" sx={{ color: '#B83B1D' }}>
          {post.category}
        </Box>
        <Box component="span" sx={{ opacity: 0.35 }}>·</Box>
        <Box component="span">{post.date}</Box>
        <Box component="span" sx={{ opacity: 0.35 }}>·</Box>
        <Box component="span">{post.readTime}</Box>
      </Box>

      {/* Title — sans, scannable. Serif is reserved for landing-page H2s. */}
      <Typography
        component="h1"
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: titleSize,
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
          color: 'text.primary',
          mb: { xs: 3, md: compact ? 3 : 3.5 },
        }}
      >
        {post.title}
      </Typography>

      {/* Subtle accent rule */}
      <Box
        sx={{
          width: 48,
          height: 2,
          background: 'linear-gradient(90deg, #B83B1D 0%, #D4694E 100%)',
          borderRadius: 1,
          mb: { xs: 4, md: compact ? 4 : 5 },
        }}
      />

      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {post.content.map((block, i) => {
          if (block.type === 'image') {
            return (
              <Box key={i} component="figure" sx={{ m: 0, my: { xs: 3, md: 4 } }}>
                <Box
                  component="img"
                  src={block.src}
                  alt={block.alt || ''}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '12px',
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                  }}
                />
                {block.caption && (
                  <Box
                    component="figcaption"
                    sx={{
                      mt: 1.25,
                      fontFamily: BRAND_SANS_FONT,
                      fontSize: '0.78rem',
                      color: alpha(theme.palette.text.primary, 0.55),
                      textAlign: 'center',
                    }}
                  >
                    {block.caption}
                  </Box>
                )}
              </Box>
            );
          }
          if (block.type === 'heading') {
            return (
              <Typography
                key={i}
                component="h2"
                sx={{
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: headingSize,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: 'text.primary',
                  mt: { xs: compact ? 4 : 5, md: compact ? 4.5 : 6 },
                  mb: 2.25,
                }}
              >
                {block.text}
              </Typography>
            );
          }
          return (
            <Typography
              key={i}
              sx={{
                fontFamily: BRAND_SANS_FONT,
                // Explicit fontWeight: 400 overrides any cascade. Body
                // paragraphs in the blog should never read as bold —
                // only the H1 title (700) and H2 section headings (600)
                // carry weight.
                fontWeight: 400,
                fontSize: bodyBody,
                lineHeight: 1.85,
                color: alpha(theme.palette.text.primary, 0.82),
                letterSpacing: '-0.005em',
                mb: 2.5,
                '&:first-of-type': {
                  fontSize: leadBody,
                  fontWeight: 400,
                  color: alpha(theme.palette.text.primary, 0.9),
                  lineHeight: 1.75,
                },
                '& strong, & b': {
                  fontWeight: 500,
                },
              }}
            >
              {block.text}
            </Typography>
          );
        })}
      </Box>
    </>
  );
}
